import { FC } from "react";

import { Question } from "capitalrx-types";
import { Dropdown, RadioGroup } from "capitalrx-components-2.0/lib/forms";

import { ANSWER_NOT_AVAILABLE, QuestionPathStatus } from "@/utils";

import { Freeform, InputNumber, RadioMultiple, SelectDropdown } from "../QuestionComponent";
import styles from "../../ClinicalCriteria.module.scss";


interface Props {
  readonly question: Question;
  readonly questionnaireId: number;
  readonly idx: number;
  readonly isReadonly: boolean;
  readonly isPreview?: boolean;
  readonly renderAnswerChoicesOnly?: boolean;
  debugMode?: boolean;
  questionPathStatus?: QuestionPathStatus;
}

export const GenerateQuestion: FC<Props> = ({
  question,
  questionnaireId,
  idx,
  isReadonly,
  isPreview = false,
  renderAnswerChoicesOnly = false,
  debugMode = false,
  questionPathStatus,
}) => {
  const question_type = question.question_type.toLowerCase();

  function getLabel(label: string, answer_choice: string) {

    if (!debugMode) {
      return label;
    }

    const next = question["next"][answer_choice] || "not-specified";

    return `${label} (next: ${JSON.stringify(next)})`;

  }

  if (question.is_internal) {
    /* We don't want to show internal questions
       This will effectively end the questionnaire
       as the user will not be able to answer it and move onto next question */
    return <></>;
  }

  const commonProps = {
    debugMode,
    isReadonly,
    isInternal: question.is_internal,
    name: `${questionnaireId}_${question.question_id}`,
    questionNumber: idx,
    questionPathStatus,
    questionText: question.question_text,
  };

  switch (question_type) {
    case "yes_no":
      return (
        <RadioMultiple
          radioLabel={question.note}
          renderAnswerChoicesOnly={renderAnswerChoicesOnly}
          {...commonProps}
        >
          <RadioGroup.Option value="yes">{getLabel("Yes", "yes")}</RadioGroup.Option>
          <RadioGroup.Option value="no">{getLabel("No", "no")}</RadioGroup.Option>
          <RadioGroup.Option value={ANSWER_NOT_AVAILABLE}>Answer not available</RadioGroup.Option>
        </RadioMultiple>
      );

    case "single_select_with_other":
      if (question.answer_choices.length <= 4 || isPreview) {
        return (
          <RadioMultiple
            radioLabel={question.note}
            renderAnswerChoicesOnly={renderAnswerChoicesOnly}
            {...commonProps}
          >
            {question.answer_choices.map((curAnswerChoice) => {
              return <RadioGroup.Option value={curAnswerChoice.answer_choice_id} key={curAnswerChoice.answer_choice_id} >{getLabel(curAnswerChoice?.answer_choice_text, curAnswerChoice?.answer_choice_id)}</RadioGroup.Option>;
            })}
            <RadioGroup.Option value="other">{getLabel("Other", "other")}</RadioGroup.Option>
            <RadioGroup.Option value={ANSWER_NOT_AVAILABLE}>Answer not available</RadioGroup.Option>
          </RadioMultiple>
        );
      } else {
        return (
          <SelectDropdown
            selectLabel={question.note}
            renderAnswerChoicesOnly={renderAnswerChoicesOnly}
            {...commonProps}
          >
            {question.answer_choices.map((curAnswerChoice) => {
              return (
                <Dropdown.Option value={curAnswerChoice.answer_choice_id} key={curAnswerChoice.answer_choice_id}>
                  <span className={styles.BreakSpaces}>{getLabel(curAnswerChoice?.answer_choice_text, curAnswerChoice?.answer_choice_id)}</span>
                </Dropdown.Option>
              );
            })}
            <Dropdown.Option value="other">{getLabel("Other", "other")}</Dropdown.Option>
            <Dropdown.Option value={ANSWER_NOT_AVAILABLE}>Answer not available</Dropdown.Option>
          </SelectDropdown>
        );
      }

    case "single_select":
      if (question.answer_choices.length <= 5 || isPreview) {
        return (
          <RadioMultiple
            radioLabel={question.note}
            renderAnswerChoicesOnly={renderAnswerChoicesOnly}
            {...commonProps}
          >
            {question?.answer_choices.map((curAnswerChoice) =>
              <RadioGroup.Option value={curAnswerChoice?.answer_choice_id} key={curAnswerChoice.answer_choice_id}>{getLabel(curAnswerChoice?.answer_choice_text, curAnswerChoice?.answer_choice_id)}</RadioGroup.Option>,
            )}
            <RadioGroup.Option value={ANSWER_NOT_AVAILABLE}>Answer not available</RadioGroup.Option>
          </RadioMultiple>
        );
      } else {
        return (
          <SelectDropdown
            selectLabel={question.note}
            renderAnswerChoicesOnly={renderAnswerChoicesOnly}
            {...commonProps}
          >
            {question.answer_choices.map((curAnswerChoice) => {
              return (
                <Dropdown.Option value={curAnswerChoice.answer_choice_id} key={curAnswerChoice.answer_choice_id}>
                  <span className={styles.BreakSpaces}>{getLabel(curAnswerChoice.answer_choice_text, curAnswerChoice.answer_choice_id)}</span>
                </Dropdown.Option>
              );
            })}
            <Dropdown.Option value={ANSWER_NOT_AVAILABLE}>Answer not available</Dropdown.Option>
          </SelectDropdown>
        );
      }
    case "numeric":
      return (
        <InputNumber
          inputLabel={question.note}
          next={question.next}
          {...commonProps}
        />
      );
    case "freeform":
      return (
        <Freeform
          questionNote={question.note}
          renderAnswerChoicesOnly={renderAnswerChoicesOnly}
          {...commonProps}
        />
      );
    default:
      return <></>;
  }
};
